"use client";

import { m as motion } from "framer-motion";
import Image from "next/image";
import { Zap, Clock } from "lucide-react";

interface AbbonamentoHeroVisualProps {
  isItalian: boolean;
}

// 5 weeks x 7 days  intensity 0-5 (Mon-Sun)
const activityGrid = [
  [0, 3, 4, 3, 4, 1, 0],
  [4, 5, 3, 5, 4, 0, 0],
  [3, 4, 5, 4, 5, 2, 0],
  [5, 4, 4, 5, 3, 0, 0],
  [4, 5, 3, 4, 5, 1, 0],
];

const techStack = ["React", "TypeScript", "Node.js", "WordPress", "Supabase"];

const deploys = [
  { it: "Feature auth v2.1", en: "Feature auth v2.1", timeIt: "2h fa", timeEn: "2h ago" },
  { it: "Fix performance API", en: "API performance fix", timeIt: "ieri", timeEn: "yesterday" },
  { it: "Dashboard analytics", en: "Analytics dashboard", timeIt: "3gg fa", timeEn: "3d ago" },
];

export function AbbonamentoHeroVisual({ isItalian }: AbbonamentoHeroVisualProps) {
  return (
    <div className="relative w-full max-w-full sm:max-w-[340px] md:max-w-[360px] xl:max-w-[380px] mx-auto pb-8 pointer-events-none select-none">
      {/* Ambient glow */}
      <div className="absolute top-1/3 right-0 w-48 h-48 rounded-full bg-primary/8 blur-3xl pointer-events-none" />

      {/* Main card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.7 }}
        className="relative rounded-2xl border border-primary/20 bg-surface shadow-[0_12px_48px_hsl(var(--primary)/0.15)] p-5"
      >
        {/* Developer header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="relative w-10 h-10 rounded-full overflow-hidden shrink-0 shadow-[0_0_16px_hsl(var(--primary)/0.35)] border border-primary/30">
            <Image
              src="/about/dario-portrait-9x16.webp"
              alt="Dario M.B."
              fill
              sizes="40px"
              className="object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-sm font-semibold text-foreground">Dario M.B.</span>
              <div className="flex items-center gap-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--success))] opacity-70" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[hsl(var(--success))]" />
                </span>
                <span className="text-[10px] text-[hsl(var(--success))] font-medium">Online</span>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground">Full-stack Developer</div>
          </div>
          <div className="text-[9px] text-muted-foreground/55 font-mono shrink-0">
            {isItalian ? "5+ anni" : "5+ yrs"}
          </div>
        </div>

        {/* Activity grid */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[9px] text-muted-foreground/55 font-mono uppercase tracking-[0.12em]">
              {isItalian ? "Attività mensile" : "Monthly activity"}
            </span>
            <span className="text-[9px] text-primary font-mono">+127 commit</span>
          </div>
          <div className="grid grid-cols-7 gap-[3px]">
            {activityGrid.map((week, wi) =>
              week.map((intensity, di) => (
                <motion.div
                  key={`${wi}-${di}`}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + (wi * 7 + di) * 0.007, duration: 0.2 }}
                  className="aspect-square rounded-[2px]"
                  style={{
                    backgroundColor:
                      intensity === 0
                        ? "hsl(var(--surface-hover))"
                        : `hsl(var(--primary) / ${(intensity / 5) * 0.8 + 0.15})`,
                  }}
                />
              ))
            )}
          </div>
        </div>

        {/* Tech stack */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {techStack.map((tech) => (
            <span
              key={tech}
              className="text-[9px] font-mono bg-background/70 border border-[hsl(var(--border))] rounded-md px-1.5 py-0.5 text-muted-foreground"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Recent deploys */}
        <div>
          <span className="text-[9px] text-muted-foreground/55 font-mono uppercase tracking-[0.12em] mb-2 block">
            {isItalian ? "Deploy recenti" : "Recent deploys"}
          </span>
          <div className="space-y-2">
            {deploys.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + i * 0.1 }}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success))] shrink-0" />
                  <span className="text-[10px] text-foreground/80 truncate">
                    {isItalian ? d.it : d.en}
                  </span>
                </div>
                <span className="text-[9px] text-muted-foreground/50 font-mono shrink-0 whitespace-nowrap">
                  {isItalian ? d.timeIt : d.timeEn}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Floating chip  hours/month */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.0 }}
        className="absolute bottom-2 left-4 flex items-center gap-2 bg-surface border border-[hsl(var(--border))] rounded-full px-3 py-1.5 shadow-md"
      >
        <Zap className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] font-semibold text-foreground whitespace-nowrap">
          {isItalian ? "1 task attivo" : "1 active task"}
        </span>
      </motion.div>

      {/* Floating chip  response time */}
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-2 right-4 flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5"
      >
        <Clock className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] font-semibold text-primary whitespace-nowrap">&lt; 24h</span>
      </motion.div>
    </div>
  );
}
